import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { FileInterface } from 'src/model/file.interface';
import { IAPISet, TxSet } from './../model/set.model';
import { FileAdapter, FileUrlBuilder } from './file.service';
import { TransactionAdapter } from './transaction.service';

export interface SetCount {
  companyCode: string,
  count: number,
  id: number,
  priority: number
}

export interface TxSetOptions {
  startDate?: Date,
  endDate?: Date,
  valid?: boolean,
  checked?: boolean,
  pending?: boolean,
  includePending?: boolean,
  type?: number,
  priority?: number,
  subtype?: number,
  saved?: boolean,
  limit?: number,
  sortASC?: boolean,
  companyId?: number,
  maxPriority?: number,
  minPriority?: number
}

@Injectable({
  providedIn: 'root'
})
export class SetAdapter {
  private transAdapter = inject(TransactionAdapter);

  adaptCount(item: any): SetCount {
    const result: SetCount = {
      companyCode: item.companyCode,
      count: item.count,
      id: item.id,
      priority: item.priority
    }
    return result;
  }

  adaptSetRemark(item: any, set: TxSet): TxSet {
    set.SetRemark = item.remark;
    set.Tags = item.tags;
    set.Saved = item.saved;
    return set;
  }

  toApiModelSetRemark(set: TxSet) {
    return {
      remark: set.SetRemark,
      saved: set.Saved,
      tags: set.Tags
    }
  }

  adapt(item: any): TxSet {
    const trans = item.transactions?.map(t => this.transAdapter.adapt(t));
    return new TxSet(
      item.id,
      item.name,
      item.remark,
      item.companyId,
      trans,
      item.created_by,
      new Date(item.created_on),
      item.checker_remark,
      item.valid,
      item.checked_by,
      item.transactionIdList ?? trans?.map(t => t.ID),
      item.companyCode,
      item.saved,
      item.setRemark,
      item.tags,
      item.priority
    )
  }

  toApiModel(set: TxSet): IAPISet {
    return {
      id: set.ID,
      name: set.Name,
      remark: set.Remark,
      companyId: set.CompanyId,
      // transactions: set.TransactionList,
      createdBy: set.CreatedBy,
      createdOn: set.CreatedOn.toISOString(),
      checkerRemark: set.CheckerRemark,
      valid: set.Valid,
      checkedBy: set.CheckedBy,
      transactionIdList: set.TransactionIdList,
      companyCode: set.CompanyCode,
      saved: set.Saved,
      setRemark: set.SetRemark,
      tags: set.Tags
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class SetService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(SetAdapter);
  private sharedService = inject(SharedService);
  private fileAdapter = inject(FileAdapter);
  private fileUrl = inject(FileUrlBuilder);

  getAllTxSetsCount(o?: TxSetOptions): Promise<SetCount[]> {
    // const startDate = new Date();
    // startDate.setHours(0, 0, 0, 0);
    // const endDate = this.sharedService.getLastMsOfDate(startDate);
    o?.startDate?.setHours(0, 0, 0, 0);
    const endDate = (o?.endDate !== undefined) ? this.sharedService.getLastMsOfDate(o?.endDate) : undefined;

    return new Promise<SetCount[]>((resolve, reject) => {
      this.api.get<SetCount[]>(this.constructApi.getSetsCount(o?.pending, o?.valid, o?.includePending, o?.type, o?.subtype, o?.companyId, o?.startDate?.toISOString(), endDate?.toISOString(), o?.priority, o?.maxPriority, o?.minPriority, o?.checked)).pipe(
        map(data => data.map(d => this.adapter.adaptCount(d))),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  getAllTxSet(o?: TxSetOptions) {
    o?.startDate?.setHours(0, 0, 0, 0);
    const endDate = (o?.endDate !== undefined) ? this.sharedService.getLastMsOfDate(o?.endDate) : undefined;
    return new Promise<TxSet[]>((resolve, reject) => {
      this.api.get<TxSet[]>(this.constructApi.getAllSets(o?.startDate?.toISOString(), endDate?.toISOString(), o?.valid, o?.checked, o?.pending, o?.includePending, o?.type, o?.priority, o?.subtype, o?.saved, o?.limit, o?.sortASC)).pipe(
        map(data => data.map(d => this.adapter.adapt(d))),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  getAllCompanyTxSet(compId: number, o?: TxSetOptions): Promise<TxSet[]> {
    o?.startDate?.setHours(0, 0, 0, 0);
    const endDate = (o?.endDate !== undefined) ? this.sharedService.getLastMsOfDate(o?.endDate) : undefined;
    return new Promise<TxSet[]>((resolve, reject) => {
      this.api.get<TxSet[]>(this.constructApi.getCompanyAllSets(compId, o?.startDate?.toISOString(), endDate?.toISOString(), o?.valid, o?.checked, o?.pending, o?.includePending, o?.type, o?.subtype, o?.saved)).pipe(
        map(data => data.map(d => this.adapter.adapt(d))),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  getTxSetDetails(setId: number): Promise<TxSet> {
    return new Promise<TxSet>((resolve, reject) => {
      this.api.get<TxSet>(this.constructApi.getSingleSetDetails(setId)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  patchEditSet(setId: number, remark: string): Promise<boolean> {
    const data = { remark: remark }
    return new Promise<boolean>((resolve, reject) => {
      this.api.patch(this.constructApi.getEditSet(setId), data).subscribe({
        next: response => resolve(response !== undefined),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update receipt remark: ', err);
          reject(err);
        }
      })
    })
  }

  putSetValidation(compId: number, setId: number, valid: boolean, remark?: string): Promise<boolean> {
    let data;
    if (remark.length > 0) data = { remark: remark };
    const url = valid ? this.constructApi.getValidateSet(compId, setId) : this.constructApi.getInvalidateSet(compId, setId);
    return new Promise<boolean>((resolve, reject) => {
      this.api.put(url, data).subscribe({
        next: response => resolve(this.adapter.adapt(response).Valid === valid),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update receipt set status: ', err);
          reject(err);
        }
      })
    })
  }

  postSetRemark(set: TxSet): Promise<TxSet> {
    return new Promise<TxSet>((resolve, reject) => {
      this.api.post(this.constructApi.getAddSetRemark(set.CompanyId, set.ID), this.adapter.toApiModelSetRemark(set)).subscribe({
        next: response => resolve(this.adapter.adaptSetRemark(response, set)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add receipt set remark: ', err);
          reject(err);
        }
      })
    })
  }

  putSetRemark(set: TxSet): Promise<TxSet> {
    return new Promise<TxSet>((resolve, reject) => {
      this.api.put(this.constructApi.getAddSetRemark(set.CompanyId, set.ID), this.adapter.toApiModelSetRemark(set)).subscribe({
        next: response => resolve(this.adapter.adaptSetRemark(response, set)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update receipt set remark: ', err);
          reject(err);
        }
      })
    })
  }

  putSubmitSet(compId: number, setId: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.api.put(this.constructApi.getSubmitSet(compId, setId), {}).subscribe({
        next: response => resolve(response !== undefined),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to submit receipt set: ', err);
          reject(err);
        }
      })
    })
  }

  deleteCompanySetTransactions(compId: number, setId: number, transIds: number[]): Promise<boolean> {
    const data = { transactionIds: transIds };
    return new Promise<boolean>((resolve, reject) => {
      this.api.delete(this.constructApi.getAddOrRemoveCompanySetTransactions(compId, setId), data).subscribe({
        next: response => resolve(response !== undefined),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to delete receipt set transactions: ', err);
          reject(err);
        }
      })
    })
  }

  getSetFiles(setId: number): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(this.constructApi.getAllSetFiles(setId)).pipe(
        map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildSetFileUrl(setId))))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to retrieve receipt set files: ', err);
          reject(err);
        }
      })
    })
  }
}
